/**
 * Admin SPA for bluera-knowledge.
 *
 * Self-contained HTML with inline CSS and JS. No external dependencies.
 * Uses hash-based routing for navigation between views.
 */

import { BASE_STYLES } from '../../mcp/ui/styles.js';

const ADMIN_STYLES = `
  /* ─── Layout ─── */
  body { margin: 0; }
  #app { display: flex; flex-direction: column; height: 100vh; }

  .app-shell { display: flex; flex: 1; min-height: 0; overflow: hidden; }

  /* ─── Sidebar ─── */
  .sidebar {
    width: 220px;
    flex-shrink: 0;
    background: var(--bg-secondary);
    border-right: 1px solid var(--border-primary);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  .sidebar-brand {
    padding: var(--space-lg);
    border-bottom: 1px solid var(--border-secondary);
    display: flex;
    align-items: center;
    gap: var(--space-sm);
  }

  .sidebar-brand-icon {
    width: 28px;
    height: 28px;
    border-radius: var(--radius-md);
    background: linear-gradient(135deg, var(--accent-blue), var(--accent-purple));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .sidebar-brand-text {
    font-size: var(--font-size-md);
    font-weight: 700;
    color: var(--text-primary);
    white-space: nowrap;
  }

  .sidebar-nav { padding: var(--space-sm); flex: 1; }

  .nav-item {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-sm) var(--space-md);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    text-decoration: none;
    font-size: var(--font-size-md);
    cursor: pointer;
    transition: background var(--transition-fast), color var(--transition-fast);
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    font-family: var(--font-sans);
  }

  .nav-item:hover { background: var(--bg-tertiary); color: var(--text-primary); }
  .nav-item.active { background: rgba(88, 166, 255, 0.1); color: var(--accent-blue); }
  .nav-item svg { flex-shrink: 0; }

  .sidebar-footer {
    padding: var(--space-md) var(--space-lg);
    border-top: 1px solid var(--border-secondary);
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
  }

  /* ─── Main Content ─── */
  .main-content {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-xl) var(--space-2xl);
    min-width: 0;
  }

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-xl);
    flex-wrap: wrap;
    gap: var(--space-md);
  }

  .page-title {
    font-size: var(--font-size-2xl);
    font-weight: 700;
    color: var(--text-primary);
  }

  .page-subtitle {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    margin-top: var(--space-xs);
  }

  /* ─── Breadcrumb ─── */
  .breadcrumb {
    display: flex;
    align-items: center;
    gap: var(--space-xs);
    font-size: var(--font-size-sm);
    color: var(--text-tertiary);
    margin-bottom: var(--space-lg);
  }

  .breadcrumb a {
    color: var(--text-link);
    text-decoration: none;
    cursor: pointer;
  }

  .breadcrumb a:hover { text-decoration: underline; }

  .breadcrumb-sep {
    color: var(--text-tertiary);
    margin: 0 var(--space-xs);
  }

  .breadcrumb-current { color: var(--text-secondary); }

  /* ─── Stats bar ─── */
  .stats-bar {
    display: flex;
    gap: var(--space-lg);
    margin-bottom: var(--space-xl);
    flex-wrap: wrap;
  }

  .stat-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-lg);
    padding: var(--space-md) var(--space-lg);
    display: flex;
    flex-direction: column;
    min-width: 100px;
  }

  .stat-value {
    font-size: var(--font-size-2xl);
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.2;
  }

  .stat-label {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 2px;
  }

  /* ─── Store card grid ─── */
  .store-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: var(--space-lg);
  }

  @media (max-width: 768px) {
    .store-grid { grid-template-columns: 1fr; }
    .sidebar { display: none; }
    .main-content { padding: var(--space-lg); }
    .stats-bar { flex-direction: column; }
  }

  .store-card {
    background: var(--bg-card);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-lg);
    overflow: hidden;
    transition: border-color var(--transition-normal), box-shadow var(--transition-normal);
    display: flex;
    flex-direction: column;
    cursor: pointer;
    text-decoration: none;
    color: inherit;
  }

  .store-card:hover {
    border-color: rgba(88, 166, 255, 0.3);
    box-shadow: var(--shadow-md);
    background: var(--bg-card-hover);
  }

  .card-header {
    padding: var(--space-lg) var(--space-lg) var(--space-sm);
  }

  .card-title-row {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
  }

  .card-status { display: flex; align-items: center; flex-shrink: 0; }

  .card-title {
    font-size: var(--font-size-md);
    font-weight: 600;
    color: var(--text-primary);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .card-description {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    margin-top: var(--space-sm);
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .card-body {
    padding: var(--space-sm) var(--space-lg);
    flex: 1;
  }

  .card-location {
    color: var(--text-tertiary);
    font-size: var(--font-size-sm);
  }

  .card-tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-xs);
    margin-top: var(--space-sm);
  }

  .card-footer {
    padding: var(--space-sm) var(--space-lg) var(--space-lg);
    border-top: 1px solid var(--border-secondary);
    margin-top: auto;
  }

  /* ─── Buttons ─── */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-sm) var(--space-lg);
    border-radius: var(--radius-md);
    font-size: var(--font-size-md);
    font-weight: 500;
    font-family: var(--font-sans);
    cursor: pointer;
    border: 1px solid transparent;
    transition: background var(--transition-fast), border-color var(--transition-fast), opacity var(--transition-fast);
    white-space: nowrap;
    text-decoration: none;
  }

  .btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .btn-primary {
    background: var(--accent-blue);
    color: var(--text-inverse);
    border-color: var(--accent-blue);
  }
  .btn-primary:hover:not(:disabled) { opacity: 0.9; }

  .btn-secondary {
    background: var(--bg-tertiary);
    color: var(--text-primary);
    border-color: var(--border-primary);
  }
  .btn-secondary:hover:not(:disabled) { background: var(--bg-card-hover); }

  .btn-danger {
    background: rgba(248, 81, 73, 0.15);
    color: var(--accent-red);
    border-color: rgba(248, 81, 73, 0.3);
  }
  .btn-danger:hover:not(:disabled) { background: rgba(248, 81, 73, 0.25); }

  .btn-sm {
    padding: var(--space-xs) var(--space-md);
    font-size: var(--font-size-sm);
  }

  .btn-group {
    display: flex;
    gap: var(--space-sm);
    flex-wrap: wrap;
  }

  /* ─── Forms ─── */
  .form-group {
    margin-bottom: var(--space-lg);
  }

  .form-label {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: 500;
    color: var(--text-primary);
    margin-bottom: var(--space-xs);
  }

  .form-hint {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
    margin-top: var(--space-xs);
  }

  .form-input, .form-select, .form-textarea {
    width: 100%;
    padding: var(--space-sm) var(--space-md);
    background: var(--bg-inset);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: var(--font-size-md);
    font-family: var(--font-sans);
    transition: border-color var(--transition-fast);
    outline: none;
  }

  .form-input:focus, .form-select:focus, .form-textarea:focus {
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 2px rgba(88, 166, 255, 0.2);
  }

  .form-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='%238b949e'%3E%3Cpath d='M2 4l4 4 4-4'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 32px;
    cursor: pointer;
  }

  .form-select option { background: var(--bg-secondary); color: var(--text-primary); }

  .form-textarea { resize: vertical; min-height: 80px; }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-lg);
  }

  @media (max-width: 640px) {
    .form-row { grid-template-columns: 1fr; }
  }

  .form-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-lg);
    padding: var(--space-xl);
    max-width: 640px;
  }

  .form-error {
    color: var(--accent-red);
    font-size: var(--font-size-sm);
    margin-top: var(--space-sm);
    display: flex;
    align-items: center;
    gap: var(--space-xs);
  }

  /* ─── Store Detail ─── */
  .detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-lg);
    margin-bottom: var(--space-xl);
  }

  @media (max-width: 768px) {
    .detail-grid { grid-template-columns: 1fr; }
  }

  .detail-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-lg);
    padding: var(--space-xl);
  }

  .detail-card-full {
    grid-column: 1 / -1;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: var(--space-sm) 0;
    border-bottom: 1px solid var(--border-secondary);
  }

  .detail-row:last-child { border-bottom: none; }

  .detail-key {
    font-size: var(--font-size-sm);
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    flex-shrink: 0;
    min-width: 110px;
  }

  .detail-value {
    font-size: var(--font-size-sm);
    color: var(--text-primary);
    text-align: right;
    word-break: break-all;
    max-width: 70%;
  }

  /* ─── Search ─── */
  .search-bar {
    display: flex;
    gap: var(--space-sm);
    margin-bottom: var(--space-lg);
  }

  .search-bar .form-input {
    flex: 1;
  }

  .search-options {
    display: flex;
    gap: var(--space-lg);
    margin-bottom: var(--space-lg);
    flex-wrap: wrap;
    align-items: flex-end;
  }

  .search-option { min-width: 160px; }

  .search-meta-bar {
    display: flex;
    align-items: center;
    gap: var(--space-md);
    flex-wrap: wrap;
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    margin-bottom: var(--space-lg);
    padding: var(--space-md) var(--space-lg);
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-lg);
  }

  .confidence-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: var(--font-size-xs);
    font-weight: 500;
    text-transform: capitalize;
  }

  .confidence-high { background: var(--confidence-high-bg); color: var(--confidence-high-text); }
  .confidence-medium { background: var(--confidence-medium-bg); color: var(--confidence-medium-text); }
  .confidence-low { background: var(--confidence-low-bg); color: var(--confidence-low-text); }

  /* ─── Results list ─── */
  .results-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
  }

  .result-card {
    background: var(--bg-card);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-lg);
    overflow: hidden;
    transition: border-color var(--transition-normal);
  }

  .result-card:hover { border-color: rgba(88, 166, 255, 0.3); }

  .result-header {
    display: flex;
    gap: var(--space-md);
    padding: var(--space-lg);
  }

  .result-rank {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--text-tertiary);
    font-family: var(--font-mono);
  }

  .result-main { flex: 1; min-width: 0; }

  .result-title-row {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    margin-bottom: var(--space-xs);
  }

  .type-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: var(--radius-sm);
    font-size: 11px;
    font-weight: 700;
    font-family: var(--font-mono);
    flex-shrink: 0;
  }

  .type-fn       { background: rgba(88, 166, 255, 0.15); color: var(--accent-blue); }
  .type-class    { background: rgba(210, 153, 34, 0.15); color: var(--accent-orange); }
  .type-iface    { background: rgba(57, 210, 192, 0.15); color: var(--accent-cyan); }
  .type-type     { background: rgba(188, 140, 255, 0.15); color: var(--accent-purple); }
  .type-const    { background: rgba(63, 185, 80, 0.15); color: var(--accent-green); }
  .type-doc      { background: rgba(139, 148, 158, 0.15); color: var(--text-secondary); }
  .type-example  { background: rgba(247, 120, 186, 0.15); color: var(--accent-pink); }
  .type-default  { background: rgba(139, 148, 158, 0.1); color: var(--text-tertiary); }

  .result-name {
    font-size: var(--font-size-md);
    font-weight: 600;
    color: var(--text-primary);
    font-family: var(--font-mono);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .result-signature {
    color: var(--text-secondary);
    margin-bottom: var(--space-sm);
    font-size: var(--font-size-sm);
    font-family: var(--font-mono);
    line-height: 1.4;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-all;
  }

  .result-purpose {
    color: var(--text-secondary);
    font-size: var(--font-size-sm);
    margin-bottom: var(--space-sm);
    line-height: 1.5;
  }

  .result-meta { margin-bottom: var(--space-xs); }

  .result-location {
    color: var(--text-tertiary);
    font-size: var(--font-size-sm);
  }

  .line-number { color: var(--accent-blue); }

  .result-store {
    font-size: var(--font-size-sm);
    color: var(--text-tertiary);
  }

  .result-relevance {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    font-style: italic;
    margin-top: var(--space-xs);
    padding-left: var(--space-md);
    border-left: 2px solid var(--border-secondary);
  }

  .score-high { color: var(--score-high); }
  .score-medium { color: var(--score-medium); }
  .score-low { color: var(--score-low); }

  .result-card details { margin: 0; border-top: 1px solid var(--border-secondary); }
  .result-card details summary { padding: var(--space-sm) var(--space-lg); font-weight: 500; }
  .result-card .details-content { padding: 0 var(--space-lg) var(--space-lg); }

  .context-group, .full-group { margin-bottom: var(--space-md); }
  .context-group:last-child, .full-group:last-child { margin-bottom: 0; }

  .usage-stats { display: flex; gap: var(--space-lg); }
  .usage-stat { display: flex; align-items: baseline; gap: var(--space-xs); }
  .usage-value { font-size: var(--font-size-lg); font-weight: 700; font-family: var(--font-mono); color: var(--text-primary); }
  .usage-label { font-size: var(--font-size-xs); color: var(--text-tertiary); }

  .documentation-text {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    line-height: 1.6;
    white-space: pre-wrap;
  }

  .result-card .code-block { max-height: 400px; overflow-y: auto; font-size: var(--font-size-sm); }

  .code-block::-webkit-scrollbar { width: 6px; height: 6px; }
  .code-block::-webkit-scrollbar-track { background: transparent; }
  .code-block::-webkit-scrollbar-thumb { background: var(--border-primary); border-radius: 3px; }
  .code-block::-webkit-scrollbar-thumb:hover { background: var(--text-tertiary); }

  /* ─── Modal ─── */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
  }

  .modal {
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-xl);
    padding: var(--space-xl);
    max-width: 420px;
    width: 90%;
    box-shadow: var(--shadow-lg);
  }

  .modal-title {
    font-size: var(--font-size-lg);
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: var(--space-md);
  }

  .modal-body {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    margin-bottom: var(--space-xl);
    line-height: 1.5;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-sm);
  }

  /* ─── Toast ─── */
  .toast-container {
    position: fixed;
    top: var(--space-lg);
    right: var(--space-lg);
    z-index: 2000;
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
    pointer-events: none;
  }

  .toast {
    padding: var(--space-md) var(--space-lg);
    border-radius: var(--radius-lg);
    font-size: var(--font-size-sm);
    box-shadow: var(--shadow-lg);
    pointer-events: auto;
    animation: toast-in 300ms ease;
    max-width: 360px;
    display: flex;
    align-items: center;
    gap: var(--space-sm);
  }

  .toast-error {
    background: rgba(248, 81, 73, 0.15);
    border: 1px solid rgba(248, 81, 73, 0.3);
    color: var(--accent-red);
  }

  .toast-success {
    background: rgba(63, 185, 80, 0.15);
    border: 1px solid rgba(63, 185, 80, 0.3);
    color: var(--accent-green);
  }

  .toast-info {
    background: rgba(88, 166, 255, 0.15);
    border: 1px solid rgba(88, 166, 255, 0.3);
    color: var(--accent-blue);
  }

  @keyframes toast-in {
    from { opacity: 0; transform: translateY(-12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* ─── Spinner ─── */
  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid var(--border-primary);
    border-top-color: var(--accent-blue);
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    display: inline-block;
    flex-shrink: 0;
  }

  .spinner-lg { width: 32px; height: 32px; border-width: 3px; }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading-center {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-2xl);
    gap: var(--space-md);
    color: var(--text-secondary);
    font-size: var(--font-size-sm);
  }

  /* ─── Skeleton ─── */
  .skeleton {
    background: linear-gradient(90deg, var(--bg-tertiary) 25%, var(--bg-card-hover) 50%, var(--bg-tertiary) 75%);
    background-size: 200% 100%;
    animation: skeleton-pulse 1.5s ease-in-out infinite;
    border-radius: var(--radius-md);
  }

  @keyframes skeleton-pulse {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .skeleton-card {
    height: 180px;
    border-radius: var(--radius-lg);
  }

  .skeleton-line {
    height: 16px;
    margin-bottom: var(--space-sm);
  }

  .skeleton-line-short { width: 60%; }

  /* ─── Multi-select (store filter) ─── */
  .multi-select {
    position: relative;
  }

  .multi-select-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-sm) var(--space-md);
    background: var(--bg-inset);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: var(--font-size-md);
    cursor: pointer;
    min-height: 38px;
    font-family: var(--font-sans);
  }

  .multi-select-trigger:hover { border-color: var(--accent-blue); }

  .multi-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    z-index: 100;
    margin-top: var(--space-xs);
    max-height: 200px;
    overflow-y: auto;
    display: none;
  }

  .multi-select-dropdown.open { display: block; }

  .multi-select-option {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-sm) var(--space-md);
    cursor: pointer;
    font-size: var(--font-size-sm);
    color: var(--text-primary);
    transition: background var(--transition-fast);
  }

  .multi-select-option:hover { background: var(--bg-tertiary); }

  .multi-select-option input[type="checkbox"] {
    accent-color: var(--accent-blue);
  }

  /* ─── Mobile nav toggle ─── */
  .mobile-nav-toggle {
    display: none;
    position: fixed;
    bottom: var(--space-lg);
    right: var(--space-lg);
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--accent-blue);
    color: var(--text-inverse);
    border: none;
    cursor: pointer;
    z-index: 500;
    box-shadow: var(--shadow-lg);
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 768px) {
    .mobile-nav-toggle { display: flex; }
    .sidebar.mobile-open { display: flex; position: fixed; top: 0; left: 0; bottom: 0; z-index: 400; }
  }

  /* ─── Misc ─── */
  .inline-search-section {
    margin-top: var(--space-xl);
    padding-top: var(--space-xl);
    border-top: 1px solid var(--border-primary);
  }

  .section-title {
    font-size: var(--font-size-lg);
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: var(--space-lg);
  }

  .store-actions {
    margin-bottom: var(--space-xl);
  }

  /* ─── Documents section ─── */
  .documents-section {
    margin-top: var(--space-xl);
    padding-top: var(--space-xl);
    border-top: 1px solid var(--border-primary);
  }

  .documents-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-lg);
  }

  .documents-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
  }

  .document-item {
    background: var(--bg-card);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-lg);
    overflow: hidden;
    transition: border-color var(--transition-normal);
  }

  .document-item:hover {
    border-color: rgba(88, 166, 255, 0.3);
  }

  .document-meta {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-md) var(--space-lg);
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border-secondary);
    font-size: var(--font-size-sm);
    min-height: 40px;
  }

  .document-index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 var(--space-xs);
    background: var(--bg-inset);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-xs);
    font-family: var(--font-mono);
    color: var(--text-tertiary);
    flex-shrink: 0;
  }

  .document-path {
    color: var(--text-link);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }

  .document-chunk-label {
    color: var(--text-tertiary);
    font-size: var(--font-size-xs);
    white-space: nowrap;
  }

  .document-content {
    margin: 0;
    padding: var(--space-lg) !important;
    min-height: 80px;
    max-height: 300px;
    overflow-y: auto;
    font-size: var(--font-size-sm) !important;
    line-height: 1.7 !important;
    white-space: pre-wrap !important;
    word-break: break-word;
    border: none !important;
    border-radius: 0 !important;
    background: var(--bg-inset) !important;
  }

  .document-content::-webkit-scrollbar { width: 6px; }
  .document-content::-webkit-scrollbar-track { background: transparent; }
  .document-content::-webkit-scrollbar-thumb { background: var(--border-primary); border-radius: 3px; }
  .document-content::-webkit-scrollbar-thumb:hover { background: var(--text-tertiary); }

  .documents-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-md);
    margin-top: var(--space-xl);
    padding: var(--space-lg) 0;
    border-top: 1px solid var(--border-secondary);
  }

  .btn-sm {
    padding: var(--space-xs) var(--space-md);
    font-size: var(--font-size-sm);
  }

  .text-secondary { color: var(--text-secondary); }
  .text-tertiary { color: var(--text-tertiary); }
`;

export function renderAdminUI(): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Bluera Knowledge - Admin</title>
  <style>${BASE_STYLES}${ADMIN_STYLES}</style>
</head>
<body>
  <div id="app"></div>
  <div class="toast-container" id="toast-container"></div>
  <div id="modal-root"></div>
  <script>
  (function() {
    'use strict';

    // ─── Utilities ───

    function esc(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    function formatDate(iso) {
      if (!iso) return '';
      var d = new Date(iso);
      var now = new Date();
      var diffMs = now.getTime() - d.getTime();
      var diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return diffDays + 'd ago';
      if (diffDays < 30) return Math.floor(diffDays / 7) + 'w ago';
      if (diffDays < 365) return Math.floor(diffDays / 30) + 'mo ago';
      return Math.floor(diffDays / 365) + 'y ago';
    }

    function formatFullDate(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString();
    }

    // ─── SVG Icons ───

    var ICONS = {
      stores: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M2 2.5A2.5 2.5 0 014.5 0h8.75a.75.75 0 01.75.75v12.5a.75.75 0 01-.75.75h-2.5a.75.75 0 110-1.5h1.75v-2h-8a1 1 0 00-.714 1.7.75.75 0 01-1.072 1.05A2.495 2.495 0 012 11.5v-9zm10.5-1h-8a1 1 0 00-1 1v6.708A2.486 2.486 0 014.5 9h8V1.5zM5 12.25v3.25a.25.25 0 00.4.2l1.45-1.087a.25.25 0 01.3 0L8.6 15.7a.25.25 0 00.4-.2v-3.25a.25.25 0 00-.25-.25h-3.5a.25.25 0 00-.25.25z"/></svg>',
      search: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M10.68 11.74a6 6 0 01-7.922-8.982 6 6 0 018.982 7.922l3.04 3.04a.749.749 0 01-.326 1.275.749.749 0 01-.734-.215l-3.04-3.04zM11.5 7a4.499 4.499 0 10-8.997 0A4.499 4.499 0 0011.5 7z"/></svg>',
      plus: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M7.75 2a.75.75 0 01.75.75V7h4.25a.75.75 0 010 1.5H8.5v4.25a.75.75 0 01-1.5 0V8.5H2.75a.75.75 0 010-1.5H7V2.75A.75.75 0 017.75 2z"/></svg>',
      trash: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M6.5 1.75a.25.25 0 01.25-.25h2.5a.25.25 0 01.25.25V3h-3V1.75zm4.5 0V3h2.25a.75.75 0 010 1.5H2.75a.75.75 0 010-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675a.75.75 0 10-1.492.15l.66 6.6A1.75 1.75 0 005.405 15h5.19a1.75 1.75 0 001.741-1.575l.66-6.6a.75.75 0 00-1.492-.15l-.66 6.6a.25.25 0 01-.249.225h-5.19a.25.25 0 01-.249-.225l-.66-6.6z"/></svg>',
      refresh: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 2.5a5.487 5.487 0 00-4.131 1.869l1.204 1.204A.25.25 0 014.896 6H1.25A.25.25 0 011 5.75V2.104a.25.25 0 01.427-.177l1.38 1.38A7.001 7.001 0 0114.95 7.16a.75.75 0 11-1.49.178A5.501 5.501 0 008 2.5zM1.705 8.005a.75.75 0 01.834.656 5.501 5.501 0 009.592 2.97l-1.204-1.204a.25.25 0 01.177-.427h3.646a.25.25 0 01.25.25v3.646a.25.25 0 01-.427.177l-1.38-1.38A7.001 7.001 0 011.05 8.84a.75.75 0 01.656-.834z"/></svg>',
      repo: '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M2 2.5A2.5 2.5 0 014.5 0h8.75a.75.75 0 01.75.75v12.5a.75.75 0 01-.75.75h-2.5a.75.75 0 110-1.5h1.75v-2h-8a1 1 0 00-.714 1.7.75.75 0 01-1.072 1.05A2.495 2.495 0 012 11.5v-9zm10.5-1h-8a1 1 0 00-1 1v6.708A2.486 2.486 0 014.5 9h8V1.5zM5 12.25v3.25a.25.25 0 00.4.2l1.45-1.087a.25.25 0 01.3 0L8.6 15.7a.25.25 0 00.4-.2v-3.25a.25.25 0 00-.25-.25h-3.5a.25.25 0 00-.25.25z"/></svg>',
      file: '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M3.75 1.5a.25.25 0 00-.25.25v11.5c0 .138.112.25.25.25h8.5a.25.25 0 00.25-.25V6H9.75A1.75 1.75 0 018 4.25V1.5H3.75zm5.75.56v2.19c0 .138.112.25.25.25h2.19L9.5 2.06zM2 1.75C2 .784 2.784 0 3.75 0h5.086c.464 0 .909.184 1.237.513l3.414 3.414c.329.328.513.773.513 1.237v8.086A1.75 1.75 0 0112.25 15h-8.5A1.75 1.75 0 012 13.25V1.75z"/></svg>',
      web: '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M1.5 8a6.5 6.5 0 1113 0 6.5 6.5 0 01-13 0zM8 0a8 8 0 100 16A8 8 0 008 0zM6.379 5.227A31.17 31.17 0 006 7.5h4c-.07-.77-.2-1.53-.379-2.273a4.7 4.7 0 01-.862.22 4.5 4.5 0 01-1.038 0 4.7 4.7 0 01-.862-.22zM5.43 7.5c.05-.9.155-1.79.42-2.667a8.2 8.2 0 01-.987-.652A6.54 6.54 0 003.538 7.5H5.43zm-1.893 1.5h1.893c.05.898.155 1.787.42 2.667-.326.217-.668.41-.987.652A6.54 6.54 0 013.538 9zm6.963 0c-.05.898-.155 1.787-.42 2.667.326.217.668.41.987.652A6.54 6.54 0 0012.462 9h-1.893zm1.893-1.5h-1.893c-.05-.9-.155-1.79-.42-2.667.326-.217.668-.41.987-.652A6.54 6.54 0 0112.462 7.5zM6 9h4c-.07.77-.2 1.53-.379 2.273a4.7 4.7 0 01-.862-.22 4.5 4.5 0 01-1.038 0 4.7 4.7 0 01-.862.22A31.17 31.17 0 016 9z"/></svg>',
      brand: '<svg width="16" height="16" viewBox="0 0 16 16" fill="white"><path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm6.5-2a1.5 1.5 0 113 0v4a1.5 1.5 0 01-3 0V6z"/></svg>',
      hamburger: '<svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor"><path d="M1 2.75A.75.75 0 011.75 2h12.5a.75.75 0 010 1.5H1.75A.75.75 0 011 2.75zm0 5A.75.75 0 011.75 7h12.5a.75.75 0 010 1.5H1.75A.75.75 0 011 7.75zM1.75 12h12.5a.75.75 0 010 1.5H1.75a.75.75 0 010-1.5z"/></svg>',
      chevron: '<svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M4.7 2.3a.75.75 0 000 1.06L7.94 6.64 4.7 9.88a.75.75 0 101.06 1.06l3.87-3.87a.75.75 0 000-1.06L5.76 2.14a.75.75 0 00-1.06.16z"/></svg>'
    };

    // ─── API client ───

    function api(method, path, body) {
      var opts = { method: method, headers: { 'Content-Type': 'application/json' } };
      if (body !== undefined) opts.body = JSON.stringify(body);
      return fetch(path, opts).then(function(res) {
        return res.json().then(function(data) {
          if (!res.ok) throw new Error(data.error || 'Request failed (' + res.status + ')');
          return data;
        });
      });
    }

    // ─── Toast notifications ───

    function showToast(message, type) {
      var container = document.getElementById('toast-container');
      var toast = document.createElement('div');
      toast.className = 'toast toast-' + (type || 'info');
      toast.textContent = message;
      container.appendChild(toast);
      setTimeout(function() {
        toast.style.transition = 'opacity 300ms';
        toast.style.opacity = '0';
        setTimeout(function() { toast.remove(); }, 300);
      }, 4000);
    }

    // ─── Modal ───

    function showModal(title, body, onConfirm, confirmLabel, confirmClass) {
      var root = document.getElementById('modal-root');
      root.innerHTML =
        '<div class="modal-overlay" id="modal-overlay">' +
          '<div class="modal">' +
            '<div class="modal-title">' + esc(title) + '</div>' +
            '<div class="modal-body">' + esc(body) + '</div>' +
            '<div class="modal-actions">' +
              '<button class="btn btn-secondary" id="modal-cancel">Cancel</button>' +
              '<button class="btn ' + (confirmClass || 'btn-danger') + '" id="modal-confirm">' + esc(confirmLabel || 'Confirm') + '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
      document.getElementById('modal-cancel').onclick = function() { root.innerHTML = ''; };
      document.getElementById('modal-overlay').onclick = function(e) {
        if (e.target === e.currentTarget) root.innerHTML = '';
      };
      document.getElementById('modal-confirm').onclick = function() {
        root.innerHTML = '';
        onConfirm();
      };
    }

    function hideModal() {
      document.getElementById('modal-root').innerHTML = '';
    }

    // ─── State ───

    var state = {
      stores: [],
      storesLoaded: false,
      currentStore: null,
      searchResults: null,
      refreshTimer: null,
      mobileNavOpen: false
    };

    // ─── Type helpers ───

    function typeIcon(type) {
      switch (type) {
        case 'repo': return ICONS.repo;
        case 'file': return ICONS.file;
        case 'web': return ICONS.web;
        default: return '';
      }
    }

    function storeStatus(store) {
      if (store.status === 'indexing') return 'indexing';
      if (store.status === 'error') return 'error';
      if (store.modelId) return 'ready';
      return 'unknown';
    }

    function storeStatusLabel(store) {
      var s = storeStatus(store);
      switch (s) {
        case 'ready': return 'Ready';
        case 'indexing': return 'Indexing';
        case 'error': return 'Error';
        default: return 'Not indexed';
      }
    }

    function scoreColor(score) {
      if (score >= 0.7) return 'var(--score-high)';
      if (score >= 0.4) return 'var(--score-medium)';
      return 'var(--score-low)';
    }

    function scoreClass(score) {
      if (score >= 0.7) return 'high';
      if (score >= 0.4) return 'medium';
      return 'low';
    }

    function resultTypeIcon(type) {
      var map = {
        'function': '<span class="type-icon type-fn">fn</span>',
        'class': '<span class="type-icon type-class">C</span>',
        'interface': '<span class="type-icon type-iface">I</span>',
        'type': '<span class="type-icon type-type">T</span>',
        'const': '<span class="type-icon type-const">K</span>',
        'documentation': '<span class="type-icon type-doc">D</span>',
        'example': '<span class="type-icon type-example">E</span>'
      };
      return map[type] || '<span class="type-icon type-default">?</span>';
    }

    // ─── Routing ───

    function navigate(hash) {
      window.location.hash = hash;
    }

    function getRoute() {
      var hash = window.location.hash || '#/';
      if (hash === '#/' || hash === '#/stores') return { view: 'dashboard' };
      var storeMatch = hash.match(/^#\\/stores\\/(.+)$/);
      if (storeMatch) return { view: 'detail', id: decodeURIComponent(storeMatch[1]) };
      if (hash === '#/create') return { view: 'create' };
      if (hash.startsWith('#/search')) {
        var params = new URLSearchParams(hash.replace('#/search', '').replace('?', ''));
        return { view: 'search', query: params.get('q') || '' };
      }
      return { view: 'dashboard' };
    }

    // ─── Rendering ───

    function renderApp() {
      clearInterval(state.refreshTimer);
      state.refreshTimer = null;

      var route = getRoute();
      var navActive = route.view;

      var html =
        '<div class="app-shell">' +
          renderSidebar(navActive) +
          '<div class="main-content" id="main-content">' +
            renderLoading() +
          '</div>' +
        '</div>' +
        '<button class="mobile-nav-toggle" id="mobile-nav-toggle">' + ICONS.hamburger + '</button>';

      document.getElementById('app').innerHTML = html;

      document.getElementById('mobile-nav-toggle').onclick = function() {
        var sidebar = document.querySelector('.sidebar');
        state.mobileNavOpen = !state.mobileNavOpen;
        if (state.mobileNavOpen) {
          sidebar.classList.add('mobile-open');
        } else {
          sidebar.classList.remove('mobile-open');
        }
      };

      switch (route.view) {
        case 'dashboard': loadDashboard(); break;
        case 'detail': loadStoreDetail(route.id); break;
        case 'create': renderCreateStore(); break;
        case 'search': renderSearchView(route.query); break;
        default: loadDashboard();
      }
    }

    function renderSidebar(active) {
      return (
        '<div class="sidebar">' +
          '<div class="sidebar-brand">' +
            '<div class="sidebar-brand-icon">' + ICONS.brand + '</div>' +
            '<span class="sidebar-brand-text">Knowledge</span>' +
          '</div>' +
          '<nav class="sidebar-nav">' +
            '<a class="nav-item' + (active === 'dashboard' ? ' active' : '') + '" href="#/stores">' +
              ICONS.stores + ' Stores' +
            '</a>' +
            '<a class="nav-item' + (active === 'search' ? ' active' : '') + '" href="#/search">' +
              ICONS.search + ' Search' +
            '</a>' +
            '<a class="nav-item' + (active === 'create' ? ' active' : '') + '" href="#/create">' +
              ICONS.plus + ' Create Store' +
            '</a>' +
          '</nav>' +
          '<div class="sidebar-footer">Bluera Knowledge Admin</div>' +
        '</div>'
      );
    }

    function renderLoading() {
      return '<div class="loading-center"><div class="spinner spinner-lg"></div> Loading...</div>';
    }

    function renderSkeletonCards(n) {
      var html = '<div class="store-grid">';
      for (var i = 0; i < n; i++) {
        html += '<div class="skeleton skeleton-card"></div>';
      }
      html += '</div>';
      return html;
    }

    // ─── Dashboard ───

    function loadDashboard() {
      var main = document.getElementById('main-content');
      main.innerHTML = renderSkeletonCards(6);

      api('GET', '/api/stores').then(function(stores) {
        state.stores = stores;
        state.storesLoaded = true;
        renderDashboardContent();

        state.refreshTimer = setInterval(function() {
          api('GET', '/api/stores').then(function(s) {
            state.stores = s;
            if (getRoute().view === 'dashboard') renderDashboardContent();
          }).catch(function() {});
        }, 10000);
      }).catch(function(err) {
        main.innerHTML = '<div class="empty-state"><div class="empty-state-title">Failed to load stores</div><p>' + esc(err.message) + '</p></div>';
      });
    }

    function renderDashboardContent() {
      var main = document.getElementById('main-content');
      var stores = state.stores;

      var repoCount = 0, fileCount = 0, webCount = 0, indexedCount = 0;
      for (var i = 0; i < stores.length; i++) {
        if (stores[i].type === 'repo') repoCount++;
        if (stores[i].type === 'file') fileCount++;
        if (stores[i].type === 'web') webCount++;
        if (stores[i].modelId) indexedCount++;
      }

      var html =
        '<div class="page-header">' +
          '<div>' +
            '<h1 class="page-title">Knowledge Stores</h1>' +
            '<p class="page-subtitle">' + stores.length + ' store' + (stores.length !== 1 ? 's' : '') + ' configured</p>' +
          '</div>' +
          '<a class="btn btn-primary" href="#/create">' + ICONS.plus + ' Create Store</a>' +
        '</div>' +
        '<div class="stats-bar">' +
          renderStatCard(stores.length, 'Total') +
          renderStatCard(indexedCount, 'Indexed') +
          renderStatCard(stores.length - indexedCount, 'Pending') +
          (repoCount > 0 ? renderStatCard(repoCount, 'Repo') : '') +
          (fileCount > 0 ? renderStatCard(fileCount, 'File') : '') +
          (webCount > 0 ? renderStatCard(webCount, 'Web') : '') +
        '</div>';

      if (stores.length === 0) {
        html += '<div class="empty-state"><div class="empty-state-title">No knowledge stores</div><p>Create a store to start indexing code and documentation.</p></div>';
      } else {
        html += '<div class="store-grid">';
        for (var j = 0; j < stores.length; j++) {
          html += renderStoreCard(stores[j]);
        }
        html += '</div>';
      }

      main.innerHTML = html;
      bindCardClicks();
    }

    function renderStatCard(value, label) {
      return (
        '<div class="stat-card">' +
          '<span class="stat-value">' + value + '</span>' +
          '<span class="stat-label">' + esc(label) + '</span>' +
        '</div>'
      );
    }

    function renderStoreCard(store) {
      var st = storeStatus(store);
      var loc = store.path || store.url || '';
      var truncLoc = loc.length > 60 ? '...' + loc.slice(loc.length - 57) : loc;

      var tagsHtml = '';
      if (store.tags && store.tags.length > 0) {
        tagsHtml = '<div class="card-tags">';
        for (var i = 0; i < store.tags.length; i++) {
          tagsHtml += '<span class="tag">' + esc(store.tags[i]) + '</span>';
        }
        tagsHtml += '</div>';
      }

      var descHtml = store.description
        ? '<p class="card-description">' + esc(store.description) + '</p>'
        : '';

      var branchHtml = store.branch
        ? '<span class="meta-separator"></span><span class="mono">' + esc(store.branch) + '</span>'
        : '';

      return (
        '<div class="store-card" data-store-id="' + esc(store.id) + '">' +
          '<div class="card-header">' +
            '<div class="card-title-row">' +
              '<div class="card-status"><span class="status-dot status-' + st + '" title="' + esc(storeStatusLabel(store)) + '"></span></div>' +
              '<h3 class="card-title">' + esc(store.name) + '</h3>' +
              '<span class="badge badge-' + store.type + '">' + typeIcon(store.type) + '<span style="margin-left:4px">' + store.type + '</span></span>' +
            '</div>' +
            descHtml +
          '</div>' +
          '<div class="card-body">' +
            (loc ? '<div class="card-location mono truncate" title="' + esc(loc) + '">' + esc(truncLoc) + '</div>' : '') +
            tagsHtml +
          '</div>' +
          '<div class="card-footer"><div class="meta-row">' +
            '<span title="' + esc(store.createdAt) + '">' + formatDate(store.createdAt) + '</span>' +
            branchHtml +
          '</div></div>' +
        '</div>'
      );
    }

    function bindCardClicks() {
      var cards = document.querySelectorAll('.store-card[data-store-id]');
      for (var i = 0; i < cards.length; i++) {
        (function(card) {
          card.onclick = function() {
            navigate('#/stores/' + encodeURIComponent(card.getAttribute('data-store-id')));
          };
        })(cards[i]);
      }
    }

    // ─── Store Detail ───

    function loadStoreDetail(id) {
      var main = document.getElementById('main-content');
      main.innerHTML = renderLoading();

      api('GET', '/api/stores/' + encodeURIComponent(id)).then(function(store) {
        state.currentStore = store;
        renderStoreDetailContent(store);
      }).catch(function(err) {
        main.innerHTML =
          '<div class="breadcrumb"><a href="#/stores">Stores</a><span class="breadcrumb-sep">' + ICONS.chevron + '</span><span class="breadcrumb-current">Not found</span></div>' +
          '<div class="empty-state"><div class="empty-state-title">Store not found</div><p>' + esc(err.message) + '</p></div>';
      });
    }

    function renderStoreDetailContent(store) {
      var main = document.getElementById('main-content');
      var st = storeStatus(store);

      var rows = [];

      // URL first for repo/web stores — externally linked
      if (store.url) rows.push({ key: 'URL', value: store.url, link: true });

      rows.push({ key: 'ID', value: store.id });
      rows.push({ key: 'Name', value: store.name });
      rows.push({ key: 'Type', value: store.type });
      rows.push({ key: 'Status', value: storeStatusLabel(store) });

      if (store.path) rows.push({ key: 'Path', value: store.path });
      if (store.branch) rows.push({ key: 'Branch', value: store.branch });
      if (store.depth != null) rows.push({ key: 'Depth', value: String(store.depth) });
      if (store.maxPages != null) rows.push({ key: 'Max Pages', value: String(store.maxPages) });
      if (store.modelId) rows.push({ key: 'Model', value: store.modelId });
      if (store.schemaVersion != null) rows.push({ key: 'Schema', value: 'v' + store.schemaVersion });
      rows.push({ key: 'Created', value: formatFullDate(store.createdAt) });
      rows.push({ key: 'Updated', value: formatFullDate(store.updatedAt) });

      var detailRows = '';
      for (var i = 0; i < rows.length; i++) {
        var val = rows[i].link
          ? '<a href="' + esc(rows[i].value) + '" target="_blank" rel="noopener noreferrer" style="color:var(--text-link);text-decoration:none">' + esc(rows[i].value) + ' <span style="font-size:11px;opacity:0.6">↗</span></a>'
          : esc(rows[i].value);
        detailRows += '<div class="detail-row"><span class="detail-key">' + esc(rows[i].key) + '</span><span class="detail-value mono">' + val + '</span></div>';
      }

      var tagsHtml = '';
      if (store.tags && store.tags.length > 0) {
        tagsHtml = '<div class="detail-row"><span class="detail-key">Tags</span><span class="detail-value"><div class="card-tags">';
        for (var t = 0; t < store.tags.length; t++) {
          tagsHtml += '<span class="tag">' + esc(store.tags[t]) + '</span>';
        }
        tagsHtml += '</div></span></div>';
      }

      var descHtml = '';
      if (store.description) {
        descHtml = '<div class="detail-row"><span class="detail-key">Description</span><span class="detail-value" style="text-align:left;max-width:none;word-break:normal">' + esc(store.description) + '</span></div>';
      }

      var html =
        '<div class="breadcrumb">' +
          '<a href="#/stores">Stores</a>' +
          '<span class="breadcrumb-sep">' + ICONS.chevron + '</span>' +
          '<span class="breadcrumb-current">' + esc(store.name) + '</span>' +
        '</div>' +
        '<div class="page-header">' +
          '<div style="display:flex;align-items:center;gap:var(--space-md)">' +
            '<span class="status-dot status-' + st + '" style="width:12px;height:12px" title="' + esc(storeStatusLabel(store)) + '"></span>' +
            '<h1 class="page-title">' + esc(store.name) + '</h1>' +
            '<span class="badge badge-' + store.type + '">' + typeIcon(store.type) + '<span style="margin-left:4px">' + store.type + '</span></span>' +
          '</div>' +
        '</div>' +
        '<div class="store-actions"><div class="btn-group">' +
          '<button class="btn btn-secondary" id="btn-reindex">' + ICONS.refresh + ' Re-index</button>' +
          '<button class="btn btn-danger" id="btn-delete">' + ICONS.trash + ' Delete</button>' +
        '</div></div>' +
        '<div class="detail-grid">' +
          '<div class="detail-card">' + detailRows + '</div>' +
          '<div class="detail-card">' + descHtml + tagsHtml +
            ((!store.description && (!store.tags || store.tags.length === 0))
              ? '<div class="empty-state" style="padding:var(--space-lg) 0"><p>No description or tags</p></div>'
              : '') +
          '</div>' +
        '</div>' +
        '<div class="inline-search-section">' +
          '<h2 class="section-title">Search in ' + esc(store.name) + '</h2>' +
          '<div class="search-bar">' +
            '<input class="form-input" id="detail-search-input" type="text" placeholder="Search within this store..." />' +
            '<button class="btn btn-primary" id="detail-search-btn">' + ICONS.search + ' Search</button>' +
          '</div>' +
          '<div id="detail-search-results"></div>' +
        '</div>' +
        '<div class="documents-section">' +
          '<h2 class="section-title">Indexed Chunks</h2>' +
          '<div id="documents-container"><div class="loading-center"><div class="spinner"></div> Loading chunks...</div></div>' +
        '</div>';

      main.innerHTML = html;

      // Load documents
      loadDocuments(store.id, 0);

      // Bind actions
      document.getElementById('btn-reindex').onclick = function() {
        var btn = this;
        btn.disabled = true;
        btn.innerHTML = '<div class="spinner"></div> Indexing...';
        api('POST', '/api/stores/' + encodeURIComponent(store.id) + '/index').then(function() {
          showToast('Re-indexing complete', 'success');
          loadStoreDetail(store.id);
        }).catch(function(err) {
          showToast('Re-index failed: ' + err.message, 'error');
          btn.disabled = false;
          btn.innerHTML = ICONS.refresh + ' Re-index';
        });
      };

      document.getElementById('btn-delete').onclick = function() {
        showModal(
          'Delete Store',
          'Are you sure you want to delete "' + store.name + '"? This action cannot be undone.',
          function() {
            api('DELETE', '/api/stores/' + encodeURIComponent(store.id)).then(function() {
              showToast('Store deleted', 'success');
              navigate('#/stores');
            }).catch(function(err) {
              showToast('Delete failed: ' + err.message, 'error');
            });
          },
          'Delete',
          'btn-danger'
        );
      };

      var searchInput = document.getElementById('detail-search-input');
      var searchBtn = document.getElementById('detail-search-btn');

      function doDetailSearch() {
        var q = searchInput.value.trim();
        if (!q) return;
        var resultsDiv = document.getElementById('detail-search-results');
        resultsDiv.innerHTML = '<div class="loading-center"><div class="spinner"></div> Searching...</div>';
        api('POST', '/api/search', { query: q, stores: [store.id], detail: 'contextual' }).then(function(data) {
          resultsDiv.innerHTML = renderSearchResults(data);
        }).catch(function(err) {
          resultsDiv.innerHTML = '<div class="form-error">' + esc(err.message) + '</div>';
        });
      }

      searchBtn.onclick = doDetailSearch;
      searchInput.onkeydown = function(e) {
        if (e.key === 'Enter') doDetailSearch();
      };
    }

    function loadDocuments(storeId, offset) {
      var limit = 15;
      var container = document.getElementById('documents-container');
      if (!container) return;

      api('GET', '/api/stores/' + encodeURIComponent(storeId) + '/documents?limit=' + limit + '&offset=' + offset).then(function(data) {
        if (data.total === 0) {
          container.innerHTML = '<div class="empty-state" style="padding:var(--space-xl) 0"><p>No indexed chunks yet. Try re-indexing this store.</p></div>';
          return;
        }

        var html = '<div class="documents-header">' +
          '<span class="text-secondary" style="font-size:var(--font-size-sm)">' + data.total + ' chunks indexed</span>' +
          '<span class="text-tertiary" style="font-size:var(--font-size-xs)">Page ' + (Math.floor(offset / limit) + 1) + ' of ' + Math.ceil(data.total / limit) + '</span>' +
        '</div>';
        html += '<div class="documents-list">';
        for (var i = 0; i < data.documents.length; i++) {
          var doc = data.documents[i];
          var path = (doc.metadata && doc.metadata.path) ? doc.metadata.path : 'unknown';
          var chunkLabel = '';
          if (doc.metadata && doc.metadata.chunkIndex != null) {
            chunkLabel = '<span class="document-chunk-label">chunk ' + doc.metadata.chunkIndex + ' of ' + (doc.metadata.totalChunks || '?') + '</span>';
          }
          var typeBadge = doc.metadata && doc.metadata.type
            ? '<span class="badge badge-' + (doc.metadata.type === 'web' ? 'web' : doc.metadata.type === 'chunk' ? 'file' : 'file') + '" style="font-size:10px;padding:1px 6px;margin-left:auto">' + esc(doc.metadata.type) + '</span>'
            : '';
          var preview = doc.content.length > 800 ? doc.content.substring(0, 800) + ' ... (truncated)' : doc.content;

          html += '<div class="document-item">' +
            '<div class="document-meta">' +
              '<span class="document-index">' + (offset + i + 1) + '</span>' +
              '<span class="document-path mono">' + esc(path) + '</span>' +
              chunkLabel +
              typeBadge +
            '</div>' +
            '<pre class="document-content code-block">' + esc(preview) + '</pre>' +
          '</div>';
        }
        html += '</div>';

        // Pagination
        html += '<div class="documents-pagination">';
        if (offset > 0) {
          html += '<button class="btn btn-secondary btn-sm" id="docs-prev">&larr; Previous</button>';
        }
        html += '<span class="text-secondary" style="font-size:var(--font-size-sm)">' +
          (offset + 1) + '–' + Math.min(offset + limit, data.total) + ' of ' + data.total +
        '</span>';
        if (offset + limit < data.total) {
          html += '<button class="btn btn-secondary btn-sm" id="docs-next">Next &rarr;</button>';
        }
        html += '</div>';

        container.innerHTML = html;

        // Bind pagination
        var prevBtn = document.getElementById('docs-prev');
        if (prevBtn) prevBtn.onclick = function() { loadDocuments(storeId, Math.max(0, offset - limit)); };
        var nextBtn = document.getElementById('docs-next');
        if (nextBtn) nextBtn.onclick = function() { loadDocuments(storeId, offset + limit); };
      }).catch(function(err) {
        container.innerHTML = '<div class="form-error">' + esc(err.message) + '</div>';
      });
    }

    // ─── Create Store ───

    function renderCreateStore() {
      var main = document.getElementById('main-content');

      var html =
        '<div class="breadcrumb">' +
          '<a href="#/stores">Stores</a>' +
          '<span class="breadcrumb-sep">' + ICONS.chevron + '</span>' +
          '<span class="breadcrumb-current">Create Store</span>' +
        '</div>' +
        '<h1 class="page-title" style="margin-bottom:var(--space-xl)">Create Store</h1>' +
        '<div class="form-card">' +
          '<div class="form-group">' +
            '<label class="form-label">Type</label>' +
            '<select class="form-select" id="create-type">' +
              '<option value="repo">Repository</option>' +
              '<option value="file">File / Directory</option>' +
              '<option value="web">Web</option>' +
            '</select>' +
          '</div>' +
          '<div class="form-group">' +
            '<label class="form-label">Name</label>' +
            '<input class="form-input" id="create-name" type="text" placeholder="my-project" />' +
          '</div>' +
          '<div class="form-group">' +
            '<label class="form-label">Description <span style="color:var(--text-tertiary)">(optional)</span></label>' +
            '<textarea class="form-textarea" id="create-description" placeholder="What this store contains..."></textarea>' +
          '</div>' +
          '<div id="create-dynamic-fields"></div>' +
          '<div class="form-group">' +
            '<label class="form-label">Tags <span style="color:var(--text-tertiary)">(optional, comma-separated)</span></label>' +
            '<input class="form-input" id="create-tags" type="text" placeholder="typescript, api, docs" />' +
          '</div>' +
          '<div id="create-error"></div>' +
          '<div class="btn-group" style="margin-top:var(--space-xl)">' +
            '<button class="btn btn-primary" id="create-submit">' + ICONS.plus + ' Create Store</button>' +
            '<a class="btn btn-secondary" href="#/stores">Cancel</a>' +
          '</div>' +
        '</div>';

      main.innerHTML = html;

      var typeSelect = document.getElementById('create-type');
      renderDynamicFields(typeSelect.value);
      typeSelect.onchange = function() { renderDynamicFields(this.value); };

      document.getElementById('create-submit').onclick = submitCreateStore;
    }

    function renderDynamicFields(type) {
      var container = document.getElementById('create-dynamic-fields');
      var html = '';

      switch (type) {
        case 'repo':
          html =
            '<div class="form-row">' +
              '<div class="form-group">' +
                '<label class="form-label">URL <span style="color:var(--text-tertiary)">(or path)</span></label>' +
                '<input class="form-input" id="create-url" type="text" placeholder="https://github.com/org/repo" />' +
              '</div>' +
              '<div class="form-group">' +
                '<label class="form-label">Path <span style="color:var(--text-tertiary)">(or URL)</span></label>' +
                '<input class="form-input" id="create-path" type="text" placeholder="/path/to/repo" />' +
              '</div>' +
            '</div>' +
            '<div class="form-group">' +
              '<label class="form-label">Branch <span style="color:var(--text-tertiary)">(optional)</span></label>' +
              '<input class="form-input" id="create-branch" type="text" placeholder="main" />' +
            '</div>';
          break;
        case 'file':
          html =
            '<div class="form-group">' +
              '<label class="form-label">Path</label>' +
              '<input class="form-input" id="create-path" type="text" placeholder="/path/to/directory" />' +
              '<p class="form-hint">Absolute path to the file or directory to index</p>' +
            '</div>';
          break;
        case 'web':
          html =
            '<div class="form-group">' +
              '<label class="form-label">URL</label>' +
              '<input class="form-input" id="create-url" type="text" placeholder="https://docs.example.com" />' +
            '</div>' +
            '<div class="form-row">' +
              '<div class="form-group">' +
                '<label class="form-label">Depth <span style="color:var(--text-tertiary)">(optional)</span></label>' +
                '<input class="form-input" id="create-depth" type="number" min="1" placeholder="3" />' +
                '<p class="form-hint">How many levels deep to crawl</p>' +
              '</div>' +
              '<div class="form-group">' +
                '<label class="form-label">Max Pages <span style="color:var(--text-tertiary)">(optional)</span></label>' +
                '<input class="form-input" id="create-maxpages" type="number" min="1" placeholder="50" />' +
              '</div>' +
            '</div>';
          break;
      }

      container.innerHTML = html;
    }

    function submitCreateStore() {
      var btn = document.getElementById('create-submit');
      var errorDiv = document.getElementById('create-error');
      errorDiv.innerHTML = '';

      var type = document.getElementById('create-type').value;
      var name = document.getElementById('create-name').value.trim();

      if (!name) {
        errorDiv.innerHTML = '<div class="form-error">Name is required</div>';
        return;
      }

      var body = { name: name, type: type };

      var descEl = document.getElementById('create-description');
      if (descEl && descEl.value.trim()) body.description = descEl.value.trim();

      var tagsEl = document.getElementById('create-tags');
      if (tagsEl && tagsEl.value.trim()) {
        body.tags = tagsEl.value.split(',').map(function(t) { return t.trim(); }).filter(function(t) { return t.length > 0; });
      }

      var pathEl = document.getElementById('create-path');
      if (pathEl && pathEl.value.trim()) body.path = pathEl.value.trim();

      var urlEl = document.getElementById('create-url');
      if (urlEl && urlEl.value.trim()) body.url = urlEl.value.trim();

      var branchEl = document.getElementById('create-branch');
      if (branchEl && branchEl.value.trim()) body.branch = branchEl.value.trim();

      var depthEl = document.getElementById('create-depth');
      if (depthEl && depthEl.value) body.depth = parseInt(depthEl.value, 10);

      btn.disabled = true;
      btn.innerHTML = '<div class="spinner"></div> Creating...';

      api('POST', '/api/stores', body).then(function(store) {
        showToast('Store "' + store.name + '" created', 'success');
        navigate('#/stores/' + encodeURIComponent(store.id));
      }).catch(function(err) {
        errorDiv.innerHTML = '<div class="form-error">' + esc(err.message) + '</div>';
        btn.disabled = false;
        btn.innerHTML = ICONS.plus + ' Create Store';
      });
    }

    // ─── Search View ───

    function renderSearchView(initialQuery) {
      var main = document.getElementById('main-content');

      // First load stores for the filter
      api('GET', '/api/stores').then(function(stores) {
        state.stores = stores;
        renderSearchViewContent(stores, initialQuery);
      }).catch(function() {
        renderSearchViewContent([], initialQuery);
      });
    }

    function renderSearchViewContent(stores, initialQuery) {
      var main = document.getElementById('main-content');
      var selectedStores = [];

      var storeOptions = '';
      for (var i = 0; i < stores.length; i++) {
        storeOptions +=
          '<label class="multi-select-option">' +
            '<input type="checkbox" value="' + esc(stores[i].id) + '" />' +
            '<span class="badge badge-' + stores[i].type + '" style="font-size:10px;padding:1px 6px">' + stores[i].type + '</span>' +
            esc(stores[i].name) +
          '</label>';
      }

      var html =
        '<h1 class="page-title" style="margin-bottom:var(--space-xl)">Search</h1>' +
        '<div class="search-bar">' +
          '<input class="form-input" id="search-input" type="text" placeholder="Search across all knowledge stores..." value="' + esc(initialQuery || '') + '" />' +
          '<button class="btn btn-primary" id="search-btn">' + ICONS.search + ' Search</button>' +
        '</div>' +
        '<div class="search-options">' +
          '<div class="search-option">' +
            '<label class="form-label">Detail Level</label>' +
            '<select class="form-select" id="search-detail">' +
              '<option value="minimal">Minimal</option>' +
              '<option value="contextual" selected>Contextual</option>' +
              '<option value="full">Full</option>' +
            '</select>' +
          '</div>' +
          '<div class="search-option">' +
            '<label class="form-label">Stores</label>' +
            '<div class="multi-select" id="store-filter">' +
              '<div class="multi-select-trigger" id="store-filter-trigger">All stores</div>' +
              '<div class="multi-select-dropdown" id="store-filter-dropdown">' +
                storeOptions +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div id="search-results"></div>';

      main.innerHTML = html;

      // Multi-select behavior
      var trigger = document.getElementById('store-filter-trigger');
      var dropdown = document.getElementById('store-filter-dropdown');

      trigger.onclick = function(e) {
        e.stopPropagation();
        dropdown.classList.toggle('open');
      };

      document.addEventListener('click', function closeDropdown(e) {
        if (!dropdown.contains(e.target) && e.target !== trigger) {
          dropdown.classList.remove('open');
        }
      });

      var checkboxes = dropdown.querySelectorAll('input[type="checkbox"]');
      for (var c = 0; c < checkboxes.length; c++) {
        checkboxes[c].onchange = function() {
          selectedStores = [];
          for (var k = 0; k < checkboxes.length; k++) {
            if (checkboxes[k].checked) selectedStores.push(checkboxes[k].value);
          }
          trigger.textContent = selectedStores.length === 0 ? 'All stores' : selectedStores.length + ' store' + (selectedStores.length > 1 ? 's' : '') + ' selected';
        };
      }

      function doSearch() {
        var q = document.getElementById('search-input').value.trim();
        if (!q) return;

        var detail = document.getElementById('search-detail').value;
        var resultsDiv = document.getElementById('search-results');
        resultsDiv.innerHTML = '<div class="loading-center"><div class="spinner"></div> Searching...</div>';

        // Update URL
        var newHash = '#/search?q=' + encodeURIComponent(q);
        if (window.location.hash !== newHash) {
          history.replaceState(null, '', newHash);
        }

        var searchBody = { query: q, detail: detail };
        if (selectedStores.length > 0) searchBody.stores = selectedStores;

        api('POST', '/api/search', searchBody).then(function(data) {
          resultsDiv.innerHTML = renderSearchResults(data);
        }).catch(function(err) {
          resultsDiv.innerHTML = '<div class="form-error">' + esc(err.message) + '</div>';
        });
      }

      document.getElementById('search-btn').onclick = doSearch;
      document.getElementById('search-input').onkeydown = function(e) {
        if (e.key === 'Enter') doSearch();
      };

      // Auto-search if we have a query
      if (initialQuery) {
        doSearch();
      }

      // Focus input
      document.getElementById('search-input').focus();
    }

    // ─── Search Results Renderer ───

    function renderSearchResults(data) {
      var results = data.results || [];
      var html = '';

      // Meta bar
      var metaParts = [];
      metaParts.push('<strong>' + results.length + '</strong> result' + (results.length !== 1 ? 's' : ''));
      if (data.timeMs != null) metaParts.push(data.timeMs + 'ms');
      if (data.mode) metaParts.push(esc(data.mode) + ' mode');

      var confidenceHtml = '';
      if (data.confidence) {
        var confClass = data.confidence === 'high' ? 'confidence-high' : data.confidence === 'medium' ? 'confidence-medium' : 'confidence-low';
        confidenceHtml = '<span class="meta-separator"></span><span class="confidence-badge ' + confClass + '">' + esc(data.confidence) + ' confidence</span>';
      }

      html += '<div class="search-meta-bar">';
      html += metaParts.join('<span class="meta-separator"></span>');
      html += confidenceHtml;
      html += '</div>';

      if (results.length === 0) {
        html += '<div class="empty-state"><div class="empty-state-title">No results found</div><p>Try broadening your search query or checking different stores.</p></div>';
        return html;
      }

      html += '<div class="results-list">';
      for (var i = 0; i < results.length; i++) {
        html += renderResultCard(results[i], i);
      }
      html += '</div>';

      return html;
    }

    function renderResultCard(result, index) {
      var summary = result.summary || {};
      var context = result.context;
      var full = result.full;
      var pct = Math.round((result.score || 0) * 100);
      var sc = scoreColor(result.score || 0);
      var scCls = scoreClass(result.score || 0);

      var hasContext = context && (
        (context.interfaces && context.interfaces.length > 0) ||
        (context.keyImports && context.keyImports.length > 0) ||
        (context.relatedConcepts && context.relatedConcepts.length > 0) ||
        context.usage
      );

      var hasFull = full && (
        (full.completeCode && full.completeCode.length > 0) ||
        (full.documentation && full.documentation.length > 0)
      );

      var locParts = (summary.location || '').split(':');
      var fileName = locParts[0] || '';
      var lineNum = locParts[1] || '';

      var html =
        '<div class="result-card">' +
          '<div class="result-header">' +
            '<div class="result-rank">' + (index + 1) + '</div>' +
            '<div class="result-main">' +
              '<div class="result-title-row">' +
                (summary.type ? resultTypeIcon(summary.type) : '') +
                '<span class="result-name">' + esc(summary.name || 'Unknown') + '</span>' +
                '<div class="score-bar-container">' +
                  '<div class="score-bar-track"><div class="score-bar-fill" style="width:' + pct + '%;background:' + sc + '"></div></div>' +
                  '<span class="score-label score-' + scCls + '">' + pct + '%</span>' +
                '</div>' +
              '</div>';

      if (summary.signature) {
        html += '<div class="result-signature">' + esc(summary.signature) + '</div>';
      }
      if (summary.purpose) {
        html += '<p class="result-purpose">' + esc(summary.purpose) + '</p>';
      }

      html += '<div class="result-meta meta-row">';
      if (fileName) {
        html += '<span class="result-location mono" title="' + esc(summary.location || '') + '">' + esc(fileName) + (lineNum ? '<span class="line-number">:' + esc(lineNum) + '</span>' : '') + '</span>';
      }
      if (summary.storeName) {
        html += '<span class="meta-separator"></span><span class="result-store">' + esc(summary.storeName) + '</span>';
      }
      html += '</div>';

      if (summary.relevanceReason) {
        html += '<div class="result-relevance">' + esc(summary.relevanceReason) + '</div>';
      }

      html += '</div></div>'; // close result-main, result-header

      // Expandable details
      if (hasContext || hasFull) {
        var detailLabel = (hasContext && hasFull) ? 'Context &amp; Source Code' : hasContext ? 'Context' : 'Source Code';
        html += '<details><summary>' + detailLabel + '</summary><div class="details-content">';

        if (hasContext) {
          html += renderContextSection(context);
        }
        if (hasFull) {
          html += renderFullSection(full);
        }

        html += '</div></details>';
      }

      html += '</div>'; // close result-card
      return html;
    }

    function renderContextSection(ctx) {
      var html = '';

      if (ctx.interfaces && ctx.interfaces.length > 0) {
        html += '<div class="context-group"><div class="section-label">Interfaces</div><ul class="pill-list">';
        for (var i = 0; i < ctx.interfaces.length; i++) html += '<li class="pill">' + esc(ctx.interfaces[i]) + '</li>';
        html += '</ul></div>';
      }

      if (ctx.keyImports && ctx.keyImports.length > 0) {
        html += '<div class="context-group"><div class="section-label">Key Imports</div><ul class="pill-list">';
        for (var j = 0; j < ctx.keyImports.length; j++) html += '<li class="pill">' + esc(ctx.keyImports[j]) + '</li>';
        html += '</ul></div>';
      }

      if (ctx.relatedConcepts && ctx.relatedConcepts.length > 0) {
        html += '<div class="context-group"><div class="section-label">Related Concepts</div><ul class="pill-list">';
        for (var k = 0; k < ctx.relatedConcepts.length; k++) html += '<li class="pill">' + esc(ctx.relatedConcepts[k]) + '</li>';
        html += '</ul></div>';
      }

      if (ctx.usage) {
        html +=
          '<div class="context-group"><div class="section-label">Usage</div>' +
          '<div class="usage-stats">' +
            '<span class="usage-stat"><span class="usage-value">' + ctx.usage.calledBy + '</span><span class="usage-label">called by</span></span>' +
            '<span class="usage-stat"><span class="usage-value">' + ctx.usage.calls + '</span><span class="usage-label">calls</span></span>' +
          '</div></div>';
      }

      return html;
    }

    function renderFullSection(full) {
      var html = '';

      if (full.documentation && full.documentation.length > 0) {
        html += '<div class="full-group"><div class="section-label">Documentation</div><div class="documentation-text">' + esc(full.documentation) + '</div></div>';
      }

      if (full.completeCode && full.completeCode.length > 0) {
        html += '<div class="full-group"><div class="section-label">Source Code</div><pre class="code-block"><code>' + esc(full.completeCode) + '</code></pre></div>';
      }

      return html;
    }

    // ─── Bootstrap ───

    window.addEventListener('hashchange', renderApp);
    renderApp();

  })();
  </script>
</body>
</html>`;
}
